import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';

const getBackendUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3002';
  }
  return '';
};

function CustomerDetail() {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [note, setNote] = useState('');
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null); // {url, type}
  const [docBlobUrls, setDocBlobUrls] = useState({}); // { [doc.id]: blob: url }
  const docFileRef = useRef(null);
  
  // Package session deduction states
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [newPackage, setNewPackage] = useState({ name: '', total_sessions: 10, price: '' });
  const [deductNote, setDeductNote] = useState('');
  const [selectedPackId, setSelectedPackId] = useState(null);
  const [showDeductModal, setShowDeductModal] = useState(false);

  const safeFormatDate = (dateString, formatStr) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '-' : format(date, formatStr);
  };

  useEffect(() => {
    fetchCustomer();
    fetchPackages();
    fetchServices();
  }, [id]);

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const response = await axios.get(`/api/customers/${id}/packages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPackages(response.data || []);
    } catch (error) {
      console.error('Fetch packages error:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const response = await axios.get('/api/services', {
        headers: { Authorization: `Bearer ${token}` }
      });
      let data = response.data || [];
      if (data.length === 0) {
        data = [
          { id: 'def1', name: 'Tüm Vücut Lazer Epilasyon', price: 3500 },
          { id: 'def2', name: 'Medikal Cilt Bakımı', price: 1200 },
          { id: 'def3', name: 'Hydrafacial Yoğun Bakım', price: 1800 },
          { id: 'def4', name: 'Protez Tırnak & Jel Güçlendirme', price: 800 }
        ];
      }
      setServices(data);
      if (data.length > 0) {
        setNewPackage(prev => ({
          ...prev,
          name: data[0].name,
          price: data[0].discount_price || data[0].price || ''
        }));
      }
    } catch (error) {
      console.error('Fetch services error:', error);
      const fallback = [
        { id: 'def1', name: 'Tüm Vücut Lazer Epilasyon', price: 3500 },
        { id: 'def2', name: 'Medikal Cilt Bakımı', price: 1200 },
        { id: 'def3', name: 'Hydrafacial Yoğun Bakım', price: 1800 },
        { id: 'def4', name: 'Protez Tırnak & Jel Güçlendirme', price: 800 }
      ];
      setServices(fallback);
      setNewPackage(prev => ({
        ...prev,
        name: fallback[0].name,
        price: fallback[0].price
      }));
    }
  };

  const fetchCustomer = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const response = await axios.get(`/api/customers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomer(response.data.customer);
      setAppointments(response.data.appointments || []);
      fetchDocs();
    } catch (error) {
      console.error('Fetch customer error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeductSession = async (e) => {
    e.preventDefault();
    if (!selectedPackId) return;
    try {
      const token = localStorage.getItem('crm_token');
      await axios.post(`/api/customer-packages/${selectedPackId}/deduct`, { note: deductNote }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeductModal(false);
      setDeductNote('');
      fetchPackages();
    } catch (error) {
      alert(error.response?.data?.error || 'Seans düşümü başarısız oldu');
    }
  };

  const handleAddPackage = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('crm_token');
      await axios.post(`/api/customers/${id}/packages`, newPackage, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowPackageModal(false);
      setNewPackage({ name: 'Tüm Vücut Lazer Epilasyon', total_sessions: 10, price: '' });
      fetchPackages();
    } catch (error) {
      alert(error.response?.data?.error || 'Paket eklenirken hata oluştu');
    }
  };

  const fetchDocs = async () => {
    try {
      setDocsLoading(true);
      const token = localStorage.getItem('crm_token');
      const res = await axios.get(`/api/customer-docs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocs(res.data || []);
    } catch (e) {
      console.error('Fetch docs error:', e);
    } finally {
      setDocsLoading(false);
    }
  };

  // /uploads/customer_docs statik rotasi sadece Authorization header'iyla calisiyor
  // (query'deki ?token= okunmuyor) - bu yuzden dosyalar dogrudan <img src>/<a href>
  // olarak degil, once fetch+Authorization ile indirilip blob URL'e cevrilerek
  // gosteriliyor (bkz. CallsTab.js'teki ayni desen).
  useEffect(() => {
    if (docs.length === 0) return undefined;
    const token = localStorage.getItem('crm_token');
    let cancelled = false;
    const createdUrls = [];

    (async () => {
      for (const doc of docs) {
        try {
          const res = await axios.get(`${getBackendUrl()}${doc.url}`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          });
          if (cancelled) return;
          const blobUrl = URL.createObjectURL(res.data);
          createdUrls.push(blobUrl);
          setDocBlobUrls(prev => ({ ...prev, [doc.id]: blobUrl }));
        } catch (err) {
          console.error('Doc file fetch error:', err);
        }
      }
    })();

    return () => {
      cancelled = true;
      createdUrls.forEach(u => URL.revokeObjectURL(u));
    };
  }, [docs]);

  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingDocs(true);
    try {
      const token = localStorage.getItem('crm_token');
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      await axios.post(`/api/customer-docs/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      fetchDocs();
    } catch (e) {
      alert('Yükleme hatası: ' + (e.response?.data?.error || e.message));
    } finally {
      setUploadingDocs(false);
      if (docFileRef.current) docFileRef.current.value = '';
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Bu dosyayı silmek istiyor musunuz?')) return;
    try {
      const token = localStorage.getItem('crm_token');
      await axios.delete(`/api/customer-docs/file/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocs(prev => prev.filter(d => d.id !== docId));
    } catch (e) {
      alert('Silme hatası');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('crm_token');
      await axios.post(`/api/customers/${id}/notes`, { note }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowNoteModal(false);
      setNote('');
      fetchCustomer();
    } catch (error) {
      alert('Not eklenemedi');
    }
  };

  if (loading) return <div>Yükleniyor...</div>;
  if (!customer) return <div>Müşteri bulunamadı</div>;

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge badge-warning',
      confirmed: 'badge badge-info',
      in_progress: 'badge badge-primary',
      completed: 'badge badge-success',
      cancelled_by_customer: 'badge badge-danger',
      cancelled_by_salon: 'badge badge-danger'
    };
    const labels = { pending: 'Bekliyor', confirmed: 'Onaylandı', in_progress: 'İşlemde', completed: 'Tamamlandı', cancelled_by_customer: 'İptal', cancelled_by_salon: 'İptal' };
    return <span className={badges[status] || 'badge badge-gray'}>{labels[status] || status}</span>;
  };

  const getTierBadge = (tier) => {
    const colors = {
      bronze: { bg: 'rgba(217, 119, 6, 0.15)', color: '#f59e0b', border: '1px solid rgba(217, 119, 6, 0.3)' },
      silver: { bg: 'rgba(148, 163, 184, 0.15)', color: '#cbd5e1', border: '1px solid rgba(148, 163, 184, 0.3)' },
      gold: { bg: 'rgba(234, 179, 8, 0.15)', color: '#facc15', border: '1px solid rgba(234, 179, 8, 0.3)' },
      platinum: { bg: 'rgba(0, 242, 254, 0.15)', color: '#00f2fe', border: '1px solid rgba(0, 242, 254, 0.3)' }
    };
    const style = colors[tier] || colors.bronze;
    return (
      <span style={{ 
        background: style.bg, 
        color: style.color,
        border: style.border,
        padding: '3px 10px', 
        borderRadius: '6px', 
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        display: 'inline-block'
      }}>
        {tier}
      </span>
    );
  };

  return (
    <div>
      <div className="header">
        <div>
          <Link to="/customers" className="text-muted" style={{ textDecoration: 'none' }}>← Müşteriler</Link>
          <h1 className="page-title" style={{ marginTop: '0.25rem', display: 'flex', alignItems: 'center' }}>
            {customer.first_name} {customer.last_name}
            {customer.avg_rating && (
              <span style={{ color: '#fbbf24', marginLeft: '12px', fontSize: '16px' }} title={`${Number(customer.avg_rating).toFixed(1)} / 5`}>
                {'★'.repeat(Math.round(Number(customer.avg_rating)))}{'☆'.repeat(5 - Math.round(Number(customer.avg_rating)))}
              </span>
            )}
          </h1>
        </div>
        <button className="btn btn-secondary" onClick={() => setShowNoteModal(true)}>
          + Not Ekle
        </button>
      </div>

      <div className="grid-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        <div className="card">
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1.25rem' }}>İletişim Bilgileri</h3>
          <div className="flex-col gap-1" style={{ display: 'flex', fontSize: '0.875rem' }}>
            <div className="flex justify-between py-2" style={{ borderBottom: '1px solid var(--border)' }}>
              <span className="text-muted">Telefon:</span>
              <span className="font-semibold">{customer.phone}</span>
            </div>
            <div className="flex justify-between py-2" style={{ borderBottom: '1px solid var(--border)' }}>
              <span className="text-muted">E-posta:</span>
              <span className="font-semibold">{customer.email || '-'}</span>
            </div>
            <div className="flex justify-between py-2" style={{ borderBottom: '1px solid var(--border)' }}>
              <span className="text-muted">Şehir:</span>
              <span className="font-semibold">{customer.city || '-'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted">Adres:</span>
              <span className="font-semibold">{customer.address || '-'}</span>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1.25rem' }}>Kişisel Bilgiler</h3>
          <div className="flex-col gap-1" style={{ display: 'flex', fontSize: '0.875rem' }}>
            <div className="flex justify-between py-2" style={{ borderBottom: '1px solid var(--border)' }}>
              <span className="text-muted">Cinsiyet:</span>
              <span className="font-semibold">{{ female: 'Kadın', male: 'Erkek', other: 'Diğer', unknown: 'Belirtilmemiş' }[customer.gender] || customer.gender || '-'}</span>
            </div>
            <div className="flex justify-between py-2" style={{ borderBottom: '1px solid var(--border)' }}>
              <span className="text-muted">Doğum Tarihi:</span>
              <span className="font-semibold">{safeFormatDate(customer.birth_date, 'dd/MM/yyyy')}</span>
            </div>
            <div className="flex justify-between py-2" style={{ borderBottom: '1px solid var(--border)' }}>
              <span className="text-muted">Cilt Tipi:</span>
              <span className="font-semibold">{customer.skin_type || '-'}</span>
            </div>
            <div className="flex justify-between py-2" style={{ borderBottom: customer.referred_by_customer_name ? '1px solid var(--border)' : 'none' }}>
              <span className="text-muted">Kaynak:</span>
              <span className="font-semibold">{{ walk_in: 'Gelip Kayıt', instagram: 'Instagram', whatsapp: 'WhatsApp', referral: 'Tavsiye', web: 'Web', phone: 'Telefon' }[customer.source] || customer.source || '-'}</span>
            </div>
            {customer.referred_by_customer_name && (
              <div className="flex justify-between py-2">
                <span className="text-muted">Referans Veren:</span>
                <span className="font-semibold" style={{ color: 'var(--primary)' }}>{customer.referred_by_customer_name}</span>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1.25rem' }}>Sadakat Programı</h3>
          <div className="flex-col gap-1" style={{ display: 'flex', fontSize: '0.875rem' }}>
            <div className="flex justify-between py-2" style={{ borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
              <span className="text-muted">Sadakat Seviyesi:</span>
              <span>{getTierBadge(customer.loyalty_tier)}</span>
            </div>
            <div className="flex justify-between py-2" style={{ borderBottom: '1px solid var(--border)' }}>
              <span className="text-muted">Toplam Puan:</span>
              <span className="font-semibold">{customer.loyalty_points} puan</span>
            </div>
            <div className="flex justify-between py-2" style={{ borderBottom: '1px solid var(--border)' }}>
              <span className="text-muted">Toplam Harcama:</span>
              <span className="font-semibold" style={{ color: 'var(--primary)' }}>{customer.total_spent} ₺</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted">Ziyaret Sayısı:</span>
              <span className="font-semibold">{customer.visit_count}</span>
            </div>
          </div>
        </div>
      </div>

      {customer.notes && (
        <div className="card mt-4">
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Müşteri Notu</h3>
          <div style={{ 
            background: 'rgba(255, 255, 255, 0.02)', 
            borderLeft: '3px solid var(--primary)', 
            borderRadius: '0 8px 8px 0', 
            padding: '12px 16px',
            fontSize: '0.875rem',
            color: 'var(--text)',
            whiteSpace: 'pre-wrap'
          }}>
            {customer.notes}
          </div>
        </div>
      )}

      {/* Satın Alınan Paketler & Seans Düşüm Arayüzü */}
      <div className="card mt-4">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>Satın Alınan Hizmet Paketleri & Seans Düşümü</h3>
          <button className="btn btn-secondary btn-sm" onClick={() => {
            if (services.length > 0) {
              setNewPackage({
                name: services[0].name,
                total_sessions: 10,
                price: services[0].discount_price || services[0].price || ''
              });
            }
            setShowPackageModal(true);
          }}>
            + Yeni Paket Tanımla
          </button>
        </div>

        {packages.length === 0 ? (
          <p className="text-muted">Tanımlı hizmet paketi bulunmuyor.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {packages.map(p => {
              const percent = Math.round((p.remaining_sessions / p.total_sessions) * 100);
              return (
                <div key={p.id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div>
                      <h4 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>{p.name}</h4>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Toplam Seans: {p.total_sessions} | Paket Ücreti: {p.price ? Number(p.price).toLocaleString('tr-TR') : '0'} ₺</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--success)' }}>{p.remaining_sessions} Kalan</span>
                      <button 
                        className="btn btn-primary btn-sm" 
                        disabled={p.remaining_sessions <= 0}
                        onClick={() => {
                          setSelectedPackId(p.id);
                          setShowDeductModal(true);
                        }}
                      >
                        ⚡ Seans Düş
                      </button>
                    </div>
                  </div>

                  <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)', height: '8px', borderRadius: '99px', overflow: 'hidden', marginBottom: '16px' }}>
                    <div style={{ width: `${percent}%`, background: 'var(--success)', height: '100%', borderRadius: '99px', transition: 'width 0.3s' }}></div>
                  </div>

                  {p.history && p.history.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <h5 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '8px' }}>Seans Kullanım Geçmişi</h5>
                      <table className="table" style={{ fontSize: '0.775rem' }}>
                        <thead>
                          <tr>
                            <th>Tarih</th>
                            <th>İşlemi Yapan</th>
                            <th>Açıklama / Not</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.history.map(h => (
                            <tr key={h.id}>
                              <td>{h.deducted_at}</td>
                              <td>{h.deducted_by}</td>
                              <td>{h.note}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="card mt-4">
        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Randevu Geçmişi</h3>
        {appointments.length === 0 ? (
          <p className="text-muted">Randevu yok</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Hizmet</th>
                <th>Personel</th>
                <th>Fiyat</th>
                <th>Durum</th>
              </tr>
            </thead>
            <tbody>
              {appointments.map(apt => (
                <tr key={apt.id}>
                  <td>{safeFormatDate(apt.scheduled_at, 'dd/MM/yyyy HH:mm')}</td>
                  <td>{apt.service_name}</td>
                  <td>{apt.staff_name}</td>
                  <td>{apt.final_price} ₺</td>
                  <td>{getStatusBadge(apt.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card mt-4">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Evrak / Belgeler</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{docs.length} dosya</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => docFileRef.current?.click()}
              disabled={uploadingDocs}
            >
              {uploadingDocs ? '⏳ Yükleniyor...' : '+ Dosya Ekle'}
            </button>
            <input
              ref={docFileRef}
              type="file"
              multiple
              accept="image/*,.pdf"
              style={{ display: 'none' }}
              onChange={handleDocUpload}
            />
          </div>
        </div>

        {docsLoading ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Yükleniyor...</p>
        ) : docs.length === 0 ? (
          <div
            onClick={() => docFileRef.current?.click()}
            style={{ border: '2px dashed var(--border)', borderRadius: 12, padding: '32px 16px', textAlign: 'center', cursor: 'pointer' }}
          >
            <div style={{ fontSize: 36 }}>📂</div>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>Henüz evrak yüklenmemiş. Buraya tıklayarak dosya ekleyin.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
            {docs.map(doc => {
              const isImage = doc.mime_type?.startsWith('image/');
              const blobUrl = docBlobUrls[doc.id];
              const openPreview = () => {
                if (!blobUrl) return;
                setPreviewDoc({ url: blobUrl, type: isImage ? 'image' : 'pdf', name: doc.original_name });
              };
              return (
                 <div
                  key={doc.id}
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', position: 'relative', transition: 'transform .2s' }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'none'}
                >
                  {/* Thumbnail */}
                  <div
                    style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.03)', cursor: blobUrl ? 'pointer' : 'default', overflow: 'hidden' }}
                    onClick={openPreview}
                  >
                    {!blobUrl ? (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Yükleniyor...</span>
                    ) : isImage ? (
                      <img
                        src={blobUrl}
                        alt={doc.original_name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    {blobUrl && (
                      <div style={{ display: isImage ? 'none' : 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 40 }}>📄</span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>PDF</span>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ padding: '10px 10px 8px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.original_name}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>{((doc.file_size || 0) / 1024).toFixed(0)} KB</div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', borderTop: '1px solid var(--border)' }}>
                    <button
                      onClick={openPreview}
                      disabled={!blobUrl}
                      style={{ flex: 1, background: 'none', border: 'none', padding: '8px 0', fontSize: 12, color: 'var(--primary)', cursor: blobUrl ? 'pointer' : 'default', fontWeight: 600, opacity: blobUrl ? 1 : 0.5 }}
                    >
                      {isImage ? '🔍 Görüntüle' : '📄 Aç'}
                    </button>
                    <div style={{ width: 1, background: 'var(--border)' }} />
                    <button
                      onClick={() => handleDeleteDoc(doc.id)}
                      style={{ flex: 1, background: 'none', border: 'none', padding: '8px 0', fontSize: 12, color: 'var(--danger)', cursor: 'pointer', fontWeight: 600 }}
                    >
                      🗑️ Sil
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showNoteModal && (
        <div className="modal-overlay" onClick={() => setShowNoteModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Not Ekle</h3>
              <button onClick={() => setShowNoteModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={handleAddNote}>
              <div className="form-group">
                <textarea
                  className="input"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  rows={4}
                  placeholder="Notunuzu yazın..."
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
                Kaydet
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Yeni Paket Ekleme Modalı */}
      {showPackageModal && (
        <div className="modal-overlay" onClick={() => setShowPackageModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Müşteriye Yeni Paket Tanımla</h3>
              <button onClick={() => setShowPackageModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={handleAddPackage}>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Hizmet Paketi Seçin</label>
                <select 
                  className="input" 
                  value={newPackage.name} 
                  onChange={e => {
                    const s = services.find(srv => srv.name === e.target.value);
                    setNewPackage({ 
                      ...newPackage, 
                      name: e.target.value,
                      price: s ? (s.discount_price || s.price || '') : ''
                    });
                  }}
                >
                  {services.map(s => (
                    <option key={s.id} value={s.name}>
                      {s.name} ({s.discount_price || s.price} ₺)
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Toplam Seans Sayısı</label>
                <input 
                  type="number" 
                  className="input" 
                  value={newPackage.total_sessions} 
                  onChange={e => setNewPackage({ ...newPackage, total_sessions: e.target.value })}
                  min="1"
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Özel Fiyat (₺)</label>
                <input 
                  type="number" 
                  className="input" 
                  value={newPackage.price} 
                  onChange={e => setNewPackage({ ...newPackage, price: e.target.value })}
                  placeholder="örn. 4500"
                  min="0"
                  step="0.01"
                />
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
                Paketi Tanımla (Kaydet)
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Seans Düşme Notu Modalı */}
      {showDeductModal && (
        <div className="modal-overlay" onClick={() => setShowDeductModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Seans Düşüm Onayı & Notu</h3>
              <button onClick={() => setShowDeductModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={handleDeductSession}>
              <p style={{ fontSize: '0.9rem', marginBottom: '1rem', color: 'var(--text-muted)' }}>
                Seçili paketten **1 seans** düşülecektir. Lütfen işlem notunu belirtin:
              </p>
              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <textarea
                  className="input"
                  value={deductNote}
                  onChange={e => setDeductNote(e.target.value)}
                  rows={3}
                  placeholder="örn. 4. Seans uygulandı, cilt durumu iyi."
                  required
                />
              </div>
              <button type="submit" className="btn btn-danger" style={{ width: '100%' }}>
                Onayla & Seans Düş
              </button>
            </form>
          </div>
        </div>
      )}
      {/* Document/Image lightbox preview */}
      {previewDoc && (
        <div
          onClick={() => setPreviewDoc(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}
        >
          <div style={{ color: '#fff', fontSize: 13, opacity: 0.6 }}>{previewDoc.name}</div>
          {previewDoc.type === 'pdf' ? (
            <iframe
              src={previewDoc.url}
              title={previewDoc.name}
              onClick={e => e.stopPropagation()}
              style={{ width: '80vw', height: '75vh', border: 'none', borderRadius: 12, background: '#fff', boxShadow: '0 24px 80px rgba(0,0,0,0.8)' }}
            />
          ) : (
            <img
              src={previewDoc.url}
              alt={previewDoc.name}
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 12, boxShadow: '0 24px 80px rgba(0,0,0,0.8)' }}
            />
          )}
          <button
            onClick={() => setPreviewDoc(null)}
            className="btn btn-secondary"
            style={{ marginTop: 8, padding: '10px 28px' }}
          >
            Kapat
          </button>
        </div>
      )}
    </div>
  );
}

export default CustomerDetail;
